import React, {Component} from "react"
import styled from "styled-components"
import {MutationFn} from "react-apollo"

import firstnameLastname from "@irati/identity/helpers/user/firstnameLastname"
import ProfilePic from "@irati/ui/src/ProfilePicture"
import {greys, fontColor} from "@irati/ui/src/global/colors"
import padding from "@irati/ui/src/global/padding"
import {breakWord} from "@irati/ui/src/global/helpers"
import {Label2, Para as ParaBase} from "@irati/ui/src/Labels"
import {FormattedRelative} from "@irati/utils/lang"
import {VoteTypeEdit} from "@irati/recognition/enums/Moderation"
import {Poll, Vote} from "@irati/types"
import ModeratePollVoteMutation from "@irati/recognition/apollo/mutation/Vote/Moderate"

import Actions from "./Actions"
import UpdateVote from "./UpdateVote"
import {ConfirmRemoveVote as ConfirmRemoveVoteBase} from "../Confirm"
import ModeratedMessage from "../../../shared/Moderated"

const ConfirmRemoveVote = styled(ConfirmRemoveVoteBase)`
  position: absolute;
  top: 0;
  right: 0;
  margin-top: ${padding.base};
  z-index: 10;
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(61, 61, 54, 0.25);
  background-color: ${greys.white};
  padding: ${padding.tiny};
  border-radius: 3px;
`

const InfoContainer = styled.div`
  flex-grow: 2;
`

const BlackCircle = styled.span`
  display: inline-block;
  font-size: 40%;
  vertical-align: middle;
  padding: 0 4px;
`

const CreatedAtDate = styled(Label2)`
  color: ${fontColor};
`

const Para = styled(ParaBase)`
  ${breakWord}
  white-space: pre-line;
`

const Footer = styled.div`
  display: flex;
  position: relative;
  justify-content: space-between;
  align-items: center;
`

interface EnablerCallback {
  (uuid: string, action: VoteTypeEdit): void
}

interface DisablerCallback {
  (): void
}

interface Props {
  poll: Poll,
  vote: Vote,
  className: string,
  isUpdateMode: boolean,
  isModerateMode: boolean,
  onEnableUpdateMode: EnablerCallback,
  onDisableUpdateMode: DisablerCallback,
  onEnableModerateMode: EnablerCallback,
  onDisableModerateMode: DisablerCallback
}

class VoteComponentBase extends Component<Props> {
  static defaultProps = {
    className: null,
    id: "vote",
    isUpdateMode: false,
    isModerateMode: false,
    onEnableUpdateMode: () => {},
    onDisableUpdateMode: () => {},
    onEnableModerateMode: () => {},
    onDisableModerateMode: () => {}
  }

  enableUpdateMode = () => {
    const {onEnableUpdateMode, vote} = this.props
    onEnableUpdateMode(vote.uuid, VoteTypeEdit.UPDATE)
  }

  disableUpdateMode = () => {
    this.props.onDisableUpdateMode()
  }

  enableModerateMode = () => {
    const {onEnableModerateMode, vote} = this.props
    onEnableModerateMode(vote.uuid, VoteTypeEdit.MODERATE)
  }

  disableModerateMode = () => {
    this.props.onDisableModerateMode()
  }

  render() {
    const {poll, vote, className, isUpdateMode, isModerateMode} = this.props

    return isUpdateMode ? (
      <UpdateVote poll={poll} vote={vote} onClickCancel={this.disableUpdateMode} onUpdateSuccess={this.disableUpdateMode} />
    ) : (
      <Container className={className}>
        <Para>
          <ProfilePic user={vote.user} marginRight regular />
          <Label2 bold>{firstnameLastname(vote.user)}</Label2>
        </Para>
        <Para>
          <ModeratedMessage message={vote.message} isModerated={vote.moderated} />
        </Para>
        <Footer>
          <InfoContainer>
            <ProfilePic user={vote.createdBy} marginRight small />
            <Label2>{firstnameLastname(vote.createdBy)}</Label2>
            <BlackCircle>&nbsp;&#9679;&nbsp;</BlackCircle>
            <CreatedAtDate>
              <FormattedRelative value={vote.updatedAt} />
            </CreatedAtDate>
          </InfoContainer>
          <Actions
            pullRight
            vote={vote}
            disabled={isUpdateMode || isModerateMode}
            onClickEdit={this.enableUpdateMode}
            onClickRemove={this.enableModerateMode} />
          {isModerateMode && (
            <ModeratePollVoteMutation poll={poll} vote={vote} message={vote.message} onCompleted={this.disableModerateMode}>
              {(mutate: MutationFn) => <ConfirmRemoveVote onCancel={this.disableModerateMode} onConfirm={mutate} />}
            </ModeratePollVoteMutation>
          )}
        </Footer>
      </Container>
    )
  }
}

export default VoteComponentBase
